import { useCallback, useMemo } from 'react';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import { playSound, Sound } from '~/assets/sounds';
import { StyledButton, StyledContainer, StyledInput } from './Counter.styled';

interface ICounterProps {
	count: number;
	minCount: number;
	maxCount: number;
	onChange: (count: number) => void;
}

const Counter: React.FC<ICounterProps> = ({
	count,
	minCount,
	maxCount,
	onChange,
}: ICounterProps) => {
	const minusButtonDisabled = useMemo(
		() => count <= minCount,
		[count, minCount],
	);
	const plusButtonDisabled = useMemo(
		() => count >= maxCount,
		[count, maxCount],
	);

	const handleValidate = useCallback(() => {
		if (count < minCount) {
			onChange(minCount);
			return;
		}

		if (count > maxCount) {
			onChange(maxCount);
		}
	}, [minCount, maxCount, onChange, count]);

	const handlePlus = useCallback(() => {
		if (count < maxCount) {
			onChange(count + 1);
		}
	}, [count, maxCount, onChange]);

	const handleMinus = useCallback(() => {
		if (count > minCount) {
			onChange(count - 1);
		}
	}, [count, minCount, onChange]);

	return (
		<StyledContainer>
			<StyledButton
				$disabled={minusButtonDisabled}
				$direction="left"
				onClick={() => {
					handleMinus();
					if (!minusButtonDisabled) {
						playSound(Sound.ClickButton);
					}
				}}
				onMouseDown={(e) => e.preventDefault()}
				onMouseEnter={() => {
					if (!minusButtonDisabled) {
						playSound(Sound.LinkCursor);
					}
				}}
			>
				<img src={arrowLeft} />
			</StyledButton>
			<StyledInput
				type={'number'}
				value={count}
				min={minCount}
				max={maxCount}
				onChange={(e) => onChange(parseInt(e.target.value))}
				onBlur={handleValidate}
				onMouseEnter={() => playSound(Sound.LinkCursor)}
			/>
			<StyledButton
				$disabled={plusButtonDisabled}
				$direction="right"
				onClick={() => {
					handlePlus();
					if (!plusButtonDisabled) {
						playSound(Sound.ClickButton);
					}
				}}
				onMouseDown={(e) => e.preventDefault()}
				onMouseEnter={() => {
					if (!plusButtonDisabled) {
						playSound(Sound.LinkCursor);
					}
				}}
			>
				<img src={arrowLeft} style={{ transform: 'rotate(180deg)' }} />
			</StyledButton>
		</StyledContainer>
	);
};

export { Counter };
